import React, {
  useState,
} from 'react';
import {
  Ref,
  Form,
  SearchInput,
  Tooltip,
  Button,
  Segment,
} from '@mairistem/composants';
import { toast } from 'react-toastify';
import {
  Entite, Sens, type Mouvement,
} from '@mairistem/comptabilite';
import { isNewIdentifiant } from '@mairistem/tools';
import {
  isEmpty, includes, isNil, find,
} from 'lodash';
import PROFIL_ECRITURE from '../../../../constants/mouvements/codeProfilEcritures';
import ProfilEcritureModal from './ProfilEcritureModal';
import ProfilEcritureResetModal from './ProfilEcritureResetModal';
import { GroupeProfilEcritureCode, GroupeProfilEcriture } from '../../../../constants/mouvements/groupesProfilEcriture';
import './ProfilEcritureSearchInput.less';
import { getProfilEcritures, searchProfilEcritures }
  from '../../../../services/piecesComptables/profilEcritures/profilEcritures';
import ProfilEcritureFactory from '../../../../classes/mouvements/profilEcriture/ProfilEcritureFactory';

interface ProfilEcritureSearchInputProps {
  codeProfil?: string;
  entite?: Entite;
  mouvement?: Mouvement;
  groupeProfilEcriture?: GroupeProfilEcriture;
  identifiant?: number | string;
  isDirty?: React.MutableRefObject<boolean>;
  onChange?: (codeProfilEcriture: string) => void;
  onClear?: (codeProfilEcriture: string) => void;
  sens?: string;
}

const PROFIL_LIBRE_RECETTE = {
  code: 'RSL', libelle: 'Avec une saisie libre', sens: Sens.RECETTE,
};

const PROFIL_LIBRE_DEPENSE = {
  code: 'DSL', libelle: 'Avec une saisie libre', sens: Sens.DEPENSE,
};

const PROFILS_LIBRE = [PROFIL_ECRITURE.RECETTE.SAI_LIB, PROFIL_ECRITURE.DEPENSE.SAI_LIB];

const ProfilEcritureSearchInput = ({
  codeProfil,
  entite,
  mouvement,
  groupeProfilEcriture,
  identifiant,
  isDirty,
  onChange,
  onClear,
  sens,
}: ProfilEcritureSearchInputProps) => {
  const [moreListOpen, setMoreListOpen] = useState(false);

  const [profils, setProfils] = useState([]);

  const [allProfils, setAllProfils] = useState([]);

  const [inputRef, setInputRef] = useState<HTMLElement>();

  const [shouldOpenTooltip, setShouldOpenTooltip] = useState<boolean>(true);

  const [libelleProfil, setLibelleProfil] = useState<string>('');

  const [open, setOpen] = useState<boolean>(false);

  const [codeProfilEcritureChosen, setCodeProfilEcritureChosen] = React.useState();

  const [defaultLibelleProfil, setDefaultLibelleProfil] = React.useState<string>();

  const [results, setResults] = React.useState([]);

  const closeMoreList = () => {
    setShouldOpenTooltip(true);
    setMoreListOpen(false);
  };

  const openMoreList = () => {
    setShouldOpenTooltip(true);
    setMoreListOpen(true);
  };

  const handleOnBeforeChange = () => setShouldOpenTooltip(false);

  const isProfilLibreCode = (code: string) => code === PROFIL_LIBRE_DEPENSE.code || code === PROFIL_LIBRE_RECETTE.code;

  const filterProfilsForMouvement = <T extends { code: string }>(
    filterProfils: T[],
    typePes: string,
  ): T[] => {
    if (!typePes) return filterProfils;

    return filterProfils.filter((p) => {
      const pe = ProfilEcritureFactory.newInstance(p.code, mouvement);
      return pe?.typeBordereau === typePes || isProfilLibreCode(pe?.code);
    });
  };

  const toSearchResults = <T extends { identifiant: number | string }>(
    items: T[],
    identifiantCourant: number | string,
  ) => items.map(({ identifiant: identifiantProfil, ...rest }: any) => ({
      key: identifiantProfil,
      identifiant: identifiantCourant,
      title: '',
      ...rest,
    }));

  // Chargement de la liste des profils d'écriture
  React.useEffect(() => {
    if (groupeProfilEcriture?.code && sens) {
      const searchParams = new URLSearchParams();

      searchParams.append('all', 'true');
      searchParams.append('groupes[]', groupeProfilEcriture.code);
      searchParams.append('sens', sens);

      getProfilEcritures(searchParams)
        .then(({ data }) => {
          setAllProfils(filterProfilsForMouvement(data, mouvement?.bordereau?.typePes));
        })
        .catch((_e) => {
          toast.error("Impossible de récupérer la liste des profils d'écriture");
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupeProfilEcriture?.code, sens, mouvement?.bordereau?.typePes]);

  const handleChangeProfil = (e, { value }) => {
    setLibelleProfil(value);
    setShouldOpenTooltip(false);

    if (isNewIdentifiant(identifiant) && !isEmpty(value)) {
      const searchParams = new URLSearchParams();

      searchParams.append('q', value);
      searchParams.append('perPage', '5');

      if (!isEmpty(sens)) {
        searchParams.append('sens', sens);
      }

      searchParams.append('groupes[]', groupeProfilEcriture?.code);

      searchProfilEcritures(searchParams)
        .then(({ data }) => {
          const filtered = filterProfilsForMouvement(data, mouvement?.bordereau?.typePes);
          setProfils(toSearchResults(filtered, identifiant));
        })
        .catch(() => {
          toast.error("Impossible de rechercher le profil d'écriture");
        });
    } else {
      setProfils([sens === Sens.DEPENSE ? PROFIL_LIBRE_DEPENSE : PROFIL_LIBRE_RECETTE]);
    }
  };

  /**
   * Restriction des choix de profils d'écritures
   */
  React.useEffect(() => {
    // Si on n'est pas sur une nouvelle pièce
    if (!isNewIdentifiant(identifiant) && !isEmpty(groupeProfilEcriture?.code)) {
      // On met les choix de profils en rapport avec le groupe du profil d'écriture
      setProfils(groupeProfilEcriture?.code === GroupeProfilEcritureCode.OPO
        ? [] : [sens === Sens.DEPENSE ? PROFIL_LIBRE_DEPENSE : PROFIL_LIBRE_RECETTE]);
    }
  }, [
    groupeProfilEcriture?.code,
    identifiant,
    sens,
  ]);

  const handleInputBlur = () => {
    setShouldOpenTooltip(true);

    if (inputRef) {
      inputRef.getElementsByTagName('input')[0].blur();
    }
  };

  const handlePress = (resetProfil: boolean) => {
    if (resetProfil) {
      onClear(codeProfilEcritureChosen);
    } else {
      setLibelleProfil(defaultLibelleProfil);
    }
    setOpen(false);
  };

  const handleSelectProfil = (e, { result }) => {
    handleInputBlur();

    // Si le code choisi, n'est pas celui déjà utilisé.
    if (codeProfil !== result?.code) {
      // Si c'est un nouvel identifiant
      if (isNewIdentifiant(identifiant)) {
        // Si on est actuellement (codeProfil) sur un profil autre que libre
        // et que le profil (code) choisi n'est pas libre
        if (!includes(PROFILS_LIBRE, codeProfil) && !includes(PROFILS_LIBRE, result?.code)) {
          setDefaultLibelleProfil(libelleProfil);
          setCodeProfilEcritureChosen(result?.code);
          // Si on a fait des modifications sur la page
          if (isDirty.current) {
            // On affiche la modal de reset
            setOpen(true);
          } else {
            onChange?.(result?.code);
          }
        } else if (onChange) {
          onChange(result?.code);
          setCodeProfilEcritureChosen(result?.code);
        }
        // Si c'est un mouvement existant
        // Si le profil choisi est un profil libre.
      } else if (includes(PROFILS_LIBRE, result?.code)) {
        if (onChange) {
          onChange(result?.code);
          setCodeProfilEcritureChosen(result?.code);
        }
      }
    }

    setShouldOpenTooltip(true);
    setMoreListOpen(false);

    setLibelleProfil(result?.libelle ?? result?.profil?.libelle);
  };

  const handleFocusProfil = () => setShouldOpenTooltip(false);

  const handleResetProfil = () => {
    setShouldOpenTooltip(true);
    if (inputRef) {
      inputRef.getElementsByTagName('input')[0].focus();
    }
  };

  const handleRef = (node) => setInputRef(node);

  const rendererProfil = ({ libelle }) => libelle || null;

  const categoryLayoutRenderer = (d) => {
    const {
      categoryContent,
      resultsContent,
    } = d;

    return (
      <Segment.Group
        // Sinon la popup ne fait pas la bonne taille
        className="profilList"
        style={{ width: (inputRef?.getBoundingClientRect()?.width ?? 100) - 2 }}
      >
        {
          (!isEmpty(categoryContent)
            && groupeProfilEcriture?.code !== GroupeProfilEcritureCode.OPO
          ) && (
            <Segment
              textAlign="center"
              inverted
              size="mini"
              className="categoryName"
            >
              {categoryContent}
            </Segment>
          )
        }
        {resultsContent}
        {
          (isNewIdentifiant(identifiant)
            && groupeProfilEcriture?.code !== GroupeProfilEcritureCode.OPO
          ) && (
            <Segment attached="bottom">
              <Button
                basic
                content="Voir toute la liste..."
                onMouseDown={openMoreList}
                size="tiny"
              />
            </Segment>
          )
        }
      </Segment.Group>
    );
  };

  React.useEffect(() => {
    // @ts-expect-error
    setResults(() => {
      if (!isEmpty(profils)) {
        return {
          search: {
            results: profils,
          },
        };
      }
      return {
        search: {
          name: "Aucun profil d'écriture correspondant",
          results: [],
        },
      };
    });
  }, [profils]);

  React.useEffect(() => {
    if (!isNil(codeProfil) && !isNewIdentifiant(identifiant)) {
      setLibelleProfil(find(allProfils, { code: codeProfil })?.libelle);
    }
  }, [
    allProfils,
    codeProfil,
    identifiant,
  ]);

  React.useEffect(() => {
    if (isNewIdentifiant(identifiant)) {
      setLibelleProfil(find(allProfils, { code: codeProfil })?.libelle);
    }
  }, [
    allProfils,
    codeProfil,
    identifiant,
  ]);

  return (
    <>
      <Tooltip
        {...!shouldOpenTooltip ? { open: false } : {}}
        // @ts-expect-error flowing
        flowing
        type="information"
        icon="question"
        position="bottom left"
        message="Le profil d'écriture définit les informations PES et enrichit certains champs quand cela est possible"
        trigger={(
          <Ref innerRef={handleRef}>
            <Form.Field
              // @ts-expect-error control
              control={SearchInput}
              label="Profil d'écriture"
              title="Profil d'écriture"
              category
              categoryLayoutRenderer={categoryLayoutRenderer}
              onBeforeChange={handleOnBeforeChange}
              onChange={handleChangeProfil}
              onSelect={handleSelectProfil}
              onReset={handleResetProfil}
              results={results}
              resultComponent={rendererProfil}
              defaultValue={libelleProfil}
              placeholder="Rechercher un profil d'écriture"
              input={{
                onFocus: handleFocusProfil,
                onBlur: handleInputBlur,
              }}
              data-testid="profilEcritureSearch"
            />
          </Ref>
        )}
      />
      <ProfilEcritureModal
        allProfils={allProfils}
        entite={entite}
        groupeProfilEcriture={groupeProfilEcriture}
        onClose={closeMoreList}
        onSelect={handleSelectProfil}
        open={moreListOpen}
        sens={sens}
      />
      <ProfilEcritureResetModal
        onPress={handlePress}
        open={open}
      />
    </>
  );
};

ProfilEcritureSearchInput.defaultProps = {
  codeProfil: null,
  sens: null,
  entite: null,
  identifiant: null,
  onChange: null,
  onClear: null,
  isDirty: {
    current: false,
  },
};

export default ProfilEcritureSearchInput;
